import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext"
import { fmtDate, relFromNow } from "@/lib/date";
import { toast } from "sonner";
import { Search, Heart, Users, BellRing, Drama, Sparkles } from "lucide-react";
import { ActorAvatar } from "@/components/ActorAvatar";
import { Seo } from "@/components/Seo";
import { Pager } from "@/components/Pager";
import { accentFor } from "@/lib/accent";

const HERO = "https://images.unsplash.com/photo-1759502418465-62fb7c781e73?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const PAGE_SIZE = 10;
const CHANGES_PER_PAGE = 5;

function SectionHeader({ icon: Icon, title, accent }) {
  return (
    <div className="mb-5 flex items-center gap-2.5">
      <div className={`flex h-8 w-8 items-center justify-center rounded-full ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
      <h2 className="font-serif text-2xl font-semibold text-foreground">{title}</h2>
    </div>
  );
}

function ActorCard({ a, i, isFollowing, toggleFollow, compact }) {
  const accent = accentFor(a.name);
  return (
    <div
      data-testid={`cast-actor-card-${a.id}`}
      className={`fade-up flex flex-col overflow-hidden rounded-2xl border bg-gradient-to-b from-[#211A27] to-[#17111C] shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
        isFollowing(a.name) ? "border-primary/60" : "border-white/10 hover:border-[#C89B3C]/50"
      } ${compact ? "w-56 flex-shrink-0" : ""}`}
      style={{ animationDelay: `${i * 40}ms` }}
    >
      <div className="h-[3px]" style={{ background: accent.bar }} />
      <div className="p-5" style={{ background: `linear-gradient(180deg, ${accent.tint}, transparent 55%)` }}>
        <div className="flex items-start justify-between">
          <Link to={`/actors/${a.id}`} className="flex items-center gap-3">
            <span className="rounded-full p-0.5" style={{ background: accent.bar }}>
              <ActorAvatar name={a.name} photoUrl={a.photo_url} size={52} testId={`cast-avatar-${a.id}`} />
            </span>
            <span className="font-serif text-lg font-semibold text-amber-50 transition-colors hover:text-[#C89B3C]">{a.name}</span>
          </Link>
          <button
            onClick={() => toggleFollow(a.name)}
            data-testid={`cast-follow-${a.id}`}
            aria-label="Follow performer"
            className={`rounded-full border p-2 transition-colors ${
              isFollowing(a.name)
                ? "border-primary bg-primary text-primary-foreground"
                : "border-white/20 text-amber-100/60 hover:border-primary hover:text-primary"
            }`}
          >
            <Heart className={`h-4 w-4 ${isFollowing(a.name) ? "fill-current" : ""}`} />
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(a.roles || []).map((r, j) => (
            <Link
              key={j}
              to={`/shows/${r.show_id}`}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-amber-50/90 transition-colors hover:border-[#C89B3C]/50 hover:text-[#C89B3C]"
            >
              {r.role} · {r.show_name}
            </Link>
          ))}
          {(!a.roles || a.roles.length === 0) && (
            <span className="text-xs text-amber-100/50">Not currently linked to a show</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Casts() {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [actors, setActors] = useState(null);
  const [changes, setChanges] = useState([]);
  const [me, setMe] = useState(null);
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState(null);
  const [page, setPage] = useState(1);
  const [changesPage, setChangesPage] = useState(1);
  const changesRef = useRef(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch {
      setMe({ followed_actors: [] });
    }
  }, []);

  const [recentCastUpdates, setRecentCastUpdates] = useState([]);
  const [shows, setShows] = useState([]);

  useEffect(() => {
    api.get("/actors").then((r) => setActors(r.data)).catch(() => setActors([]));
    api.get("/cast-changes/recent").then((r) => setChanges(r.data)).catch(() => setChanges([]));
    api.get("/cast-changes/recently-updated").then((r) => setRecentCastUpdates(r.data)).catch(() => setRecentCastUpdates([]));
    loadMe();
    api.get("/shows").then((r) => setShows(r.data)).catch(() => setShows([]));
  }, [loadMe]);

  const term = q.trim().toLowerCase();

  const matchingShows = useMemo(() => {
    if (!term) return [];
    return shows.filter((s) => s.name.toLowerCase().includes(term));
  }, [shows, term]);

  const filtered = useMemo(() => {
    if (!actors) return null;
    if (!term) return actors;
    const castNames = new Set();
    matchingShows.forEach((s) =>
      (s.cast || []).forEach((c) => castNames.add((c.actor_name || "").trim().toLowerCase()))
    );
    return actors.filter(
      (a) =>
        a.name.toLowerCase().includes(term) ||
        castNames.has(a.name.trim().toLowerCase()) ||
        (a.roles || []).some((r) => (r.show_name || "").toLowerCase().includes(term))
    );
  }, [actors, term, matchingShows]);

  const trending = useMemo(() => {
    if (!actors || recentCastUpdates.length === 0) return [];
    const seen = new Set();
    const result = [];
    for (const c of recentCastUpdates) {
      if (!c.actor_name || seen.has(c.actor_name)) continue;
      const match = actors.find((a) => a.name === c.actor_name);
      if (match) {
        seen.add(c.actor_name);
        result.push(match);
      }
      if (result.length >= 5) break;
    }
    return result;
  }, [actors, recentCastUpdates]);

  const trendingIds = useMemo(() => new Set(trending.map((a) => a.id)), [trending]);
  const rest = useMemo(
    () => (filtered || []).filter((a) => !trendingIds.has(a.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [filtered, trendingIds, term]
  );

  const availableLetters = useMemo(() => {
    const set = new Set(rest.map((a) => a.name[0]?.toUpperCase()).filter(Boolean));
    return set;
  }, [rest]);

  useEffect(() => { setLetter(null); setPage(1); }, [q]);
  useEffect(() => { setPage(1); }, [letter]);

  const shownAll = letter ? rest.filter((a) => a.name[0]?.toUpperCase() === letter) : rest;
  const paginated = letter ? shownAll : shownAll.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(shownAll.length / PAGE_SIZE));

  const followedCount = me?.followed_actors?.length || 0;

  const changesTotalPages = Math.max(1, Math.ceil(changes.length / CHANGES_PER_PAGE));
  const safeChangesPage = Math.min(changesPage, changesTotalPages);
  const pagedChanges = changes.slice(
    (safeChangesPage - 1) * CHANGES_PER_PAGE,
    safeChangesPage * CHANGES_PER_PAGE
  );
  const goToChangesPage = (p) => {
    setChangesPage(Math.max(1, Math.min(changesTotalPages, p)));
    changesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const followNow = async (name) => {
    const { data } = await api.post(`/watchlist/follow-actor/${encodeURIComponent(name)}`);
    toast.success(data.following ? `Following ${name} — you'll be emailed on cast changes` : `Unfollowed ${name}`);
    await loadMe();
  };
  const toggleFollow = (name) =>
    user ? followNow(name) : openAuthModal(() => followNow(name), `Sign in to follow ${name} and get alerts about their shows.`);

  const isFollowing = (name) => me?.followed_actors?.includes(name);

  return (
    <div>
      <Seo
        title="West End cast list and cast changes"
        description="Find the performers in West End shows, see who is joining or leaving, and follow your favourite actors for alerts."
      />
      <section className="relative overflow-hidden hero-scrim grain" style={{ "--hero-img": `url(${HERO})` }}>
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <p className="overline-label text-[#C89B3C]">The people on stage</p>
          <h1 className="mt-3 max-w-2xl font-serif text-4xl font-semibold leading-tight text-amber-50 sm:text-5xl lg:text-6xl">
            Follow your favourite West End performers.
          </h1>
          <p className="mt-4 max-w-lg text-amber-100/70">
            Search actors to see what they're in now and what's next. Follow anyone and we'll email you the moment a
            cast change is confirmed for a show they're in.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-amber-100/60">
            <span className="flex items-center gap-2"><Drama className="h-4 w-4 text-[#C89B3C]" /> {actors ? actors.length : "…"} performers tracked</span>
            <span className="flex items-center gap-2"><BellRing className="h-4 w-4 text-[#C89B3C]" /> {changes.length} recent cast changes</span>
            {followedCount > 0 && (
              <span className="flex items-center gap-2"><Heart className="h-4 w-4 text-[#C89B3C]" /> Following {followedCount}</span>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="relative mb-10 max-w-lg">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="cast-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search performers or shows…"
            className="w-full rounded-full border border-input bg-card py-3 pl-10 pr-4 outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {q && matchingShows.length > 0 && (
          <section className="mb-10">
            <SectionHeader icon={Drama} title={`Shows matching “${q.trim()}”`} accent="bg-primary/15 text-primary" />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {matchingShows.map((s) => (
                <Link
                  key={s.id}
                  to={`/shows/${s.slug || s.id}`}
                  className="flex gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:border-primary"
                >
                  {s.image_url && (
                    <img src={s.image_url} alt={s.name} className="h-20 w-14 flex-shrink-0 rounded-lg object-cover" />
                  )}
                  <div className="min-w-0">
                    <p className="font-serif text-base font-semibold leading-snug">{s.name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{s.venue}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!q && trending.length > 0 && (
          <section className="mb-12">
            <SectionHeader icon={Sparkles} title="Trending now" accent="bg-primary/15 text-primary" />
            <div className="flex gap-4 overflow-x-auto pb-2">
              {trending.map((a, i) => (
                <ActorCard key={a.id} a={a} i={i} isFollowing={isFollowing} toggleFollow={toggleFollow} compact />
              ))}
            </div>
          </section>
        )}

        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeader icon={Users} title="All Performers" accent="bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]" />

            {!q && (
              <div className="mb-6 flex flex-wrap gap-1">
                <button
                  onClick={() => setLetter(null)}
                  className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                    letter === null ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  All
                </button>
                {LETTERS.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLetter(l)}
                    disabled={!availableLetters.has(l)}
                    className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                      letter === l
                        ? "bg-primary text-primary-foreground"
                        : availableLetters.has(l)
                        ? "text-foreground hover:bg-secondary"
                        : "text-muted-foreground/30"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}

            {shownAll.length === 0 && matchingShows.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
                <Drama className="mx-auto h-9 w-9 text-muted-foreground" />
                <p className="mt-3 font-serif text-xl font-semibold">
                  {q ? "No performers match your search" : `No performers starting with "${letter}"`}
                </p>
                <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                  {q ? "Try another name or show." : "Pick another letter, or select All to see everyone."}
                </p>
              </div>
            )}

{paginated.length > 0 && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  {paginated.map((a, i) => (
                    <ActorCard key={a.id} a={a} i={i} isFollowing={isFollowing} toggleFollow={toggleFollow} />
                  ))}
                </div>

                {!letter && totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div ref={changesRef} className="scroll-mt-24">
          <SectionHeader icon={BellRing} title="Recent cast changes" accent="bg-primary/15 text-primary" />
            <div className="space-y-3">
              {changes.length === 0 && (
                <p className="rounded-xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
                  No cast changes logged yet. When one is confirmed, it appears here — and followers get an email.
                </p>
              )}
              {pagedChanges.map((e, i) => (
                <Link
                  key={e.id}
                  to={`/shows/${e.show_id}`}
                  data-testid={`cast-change-${e.id}`}
                  className="fade-up block rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-primary"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <p className="font-medium">{e.description}</p>
                  <p className="mt-1 font-serif text-sm text-muted-foreground">{e.show_name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{relFromNow(e.change_date) || fmtDate(e.change_date)}</p>
                </Link>
                ))}
              </div>
              <Pager page={safeChangesPage} totalPages={changesTotalPages} onChange={goToChangesPage} />
            </div>
          </div>
         </div>
       </div>
     );
  }